import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as LinkIcon, Search, KeyRound, CheckCircle2, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { getAvatarGradient } from '../../utils/avatarUtils';
import { DjayAvatar } from '../../../../mascot';
import { type Classroom, type LearnerAccount, type ClassroomValidationWarning } from '../../../../accounts';
// ── Reusable learner row ──
function LearnerRow({
  learner,
  isSelected,
  onToggle
}: {
  learner: LearnerAccount;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return <button key={learner.id} type="button" onClick={onToggle} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isSelected ? 'bg-violet-50 border-l-4 border-l-violet-500' : 'hover:bg-slate-50 border-l-4 border-l-transparent'}`} data-id="element-6115">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${getAvatarGradient(learner.displayName)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`} data-id="element-6116">
        {learner.displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0" data-id="element-6117">
        <div className="font-bold text-slate-900 text-sm truncate" data-id="element-6118">
          {learner.displayName}
        </div>
        <div className="text-xs text-slate-500 truncate" data-id="element-6119">
          {learner.username}
        </div>
      </div>
      {learner.grade && <div className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-md" data-id="element-6120">
          Gr {learner.grade}
        </div>}
      {isSelected && <CheckCircle2 className="w-5 h-5 text-violet-600 flex-shrink-0 ml-2" data-id="element-6121" />}
    </button>;
}
interface LinkExistingStepProps {
  activeClassroom: Classroom | null;
  availableLearners: LearnerAccount[];
  filteredLearners: LearnerAccount[];
  matchingLearners?: LearnerAccount[];
  otherLearners?: LearnerAccount[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  linkMode: 'search' | 'code';
  setLinkMode: (mode: 'search' | 'code') => void;
  selectedLearner: LearnerAccount | null;
  setSelectedLearner: (learner: LearnerAccount | null) => void;
  linkCodeInput: string;
  setLinkCodeInput: (code: string) => void;
  foundByCode: LearnerAccount | null;
  setFoundByCode: (learner: LearnerAccount | null) => void;
  formError: string;
  setFormError: (error: string) => void;
  linkWarnings: ClassroomValidationWarning[];
  confirmLink: boolean;
  setConfirmLink: (confirm: boolean) => void;
  onLookupCode: () => void;
  onLinkSelectedLearner: () => void;
  onBack: () => void;
}
export function LinkExistingStep({
  activeClassroom,
  availableLearners,
  filteredLearners,
  matchingLearners = [],
  otherLearners = [],
  searchQuery,
  setSearchQuery,
  linkMode,
  setLinkMode,
  selectedLearner,
  setSelectedLearner,
  linkCodeInput,
  setLinkCodeInput,
  foundByCode,
  setFoundByCode,
  formError,
  setFormError,
  linkWarnings,
  confirmLink,
  setConfirmLink,
  onLookupCode,
  onLinkSelectedLearner,
  onBack
}: LinkExistingStepProps) {
  const [showOtherGroups, setShowOtherGroups] = useState(false);
  return <motion.div key="existing" initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.2
  }} className="max-w-lg mx-auto" data-id="element-6122">
      <div className="text-center mb-6" data-id="element-6123">
        <div className="w-14 h-14 bg-violet-100 text-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-sm" data-id="element-6124">
          <LinkIcon className="w-7 h-7" data-id="element-6125" />
        </div>
        <h3 className="text-xl font-black text-slate-900 mb-1" data-id="element-6126">
          Bestaande leerling koppelen
        </h3>
        {activeClassroom && <p className="text-sm text-slate-500" data-id="element-6127">
            Wordt toegevoegd aan <strong data-id="element-6128">{activeClassroom.name}</strong>
          </p>}
      </div>

      {/* Sub-tabs: Search vs Code */}
      <div className="flex bg-slate-100 rounded-xl p-1 mb-6" data-id="element-6129">
        <button onClick={() => {
        setLinkMode('search');
        setFormError('');
        setFoundByCode(null);
      }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${linkMode === 'search' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6130">
          <Search className="w-4 h-4" data-id="element-6131" />
          Zoek leerling
        </button>
        <button onClick={() => {
        setLinkMode('code');
        setFormError('');
        setSelectedLearner(null);
      }} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${linkMode === 'code' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-6132">
          <KeyRound className="w-4 h-4" data-id="element-6133" />
          Koppelcode
        </button>
      </div>

      {linkMode === 'search' && <div className="space-y-4" data-id="element-6134">
          {/* Search input */}
          <Input type="text" value={searchQuery} onChange={e => {
        setSearchQuery(e.target.value);
        setSelectedLearner(null);
      }} placeholder="Zoek op naam of gebruikersnaam..." icon={<Search className="w-4 h-4" data-id="element-6136" />} variant="admin" size="lg" className="bg-slate-50 border-slate-200 rounded-xl font-medium text-slate-900 placeholder:text-slate-400 focus:ring-violet-500 focus:border-violet-500" autoFocus data-id="element-6135" />

          {/* Learner list */}
          <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-6137">
            {filteredLearners.length === 0 ? <div className="py-10 text-center text-sm text-slate-400" data-id="element-6138">
                <div className="mb-3 flex justify-center" data-id="element-6139">
                  <DjayAvatar size="md" emotion="thinking" data-id="element-6140" />
                </div>
                {availableLearners.length === 0 ? 'Alle leerlingen zijn al gekoppeld.' : 'Geen leerlingen gevonden.'}
              </div> : <div className="max-h-64 overflow-y-auto divide-y divide-slate-100" data-id="element-6141">
                {activeClassroom && matchingLearners.length > 0 && <div className="px-4 py-2 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider sticky top-0 z-10" data-id="element-6142">
                    Groep {activeClassroom.groupLevel}
                  </div>}
                {(activeClassroom ? matchingLearners : filteredLearners).map(learner => <LearnerRow key={learner.id} learner={learner} isSelected={selectedLearner?.id === learner.id} onToggle={() => setSelectedLearner(selectedLearner?.id === learner.id ? null : learner)} data-id="element-6143" />)}

                {activeClassroom && otherLearners.length > 0 && <>
                    <button type="button" onClick={() => setShowOtherGroups(!showOtherGroups)} className="w-full px-4 py-2 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider sticky top-0 z-10 flex justify-between items-center hover:bg-slate-100 transition-colors" data-id="element-6144">
                      <span data-id="element-6145">Andere groepen ({otherLearners.length})</span>
                      <span className="flex items-center gap-1" data-id="element-6146">
                        {showOtherGroups ? <>
                            Verberg <ChevronUp className="w-3 h-3" data-id="element-6147" />
                          </> : <>
                            Toon <ChevronDown className="w-3 h-3" data-id="element-6148" />
                          </>}
                      </span>
                    </button>
                    {showOtherGroups && otherLearners.map(learner => <LearnerRow key={learner.id} learner={learner} isSelected={selectedLearner?.id === learner.id} onToggle={() => setSelectedLearner(selectedLearner?.id === learner.id ? null : learner)} data-id="element-6149" />)}
                  </>}
              </div>}
          </div>

          {formError && <div className="text-sm text-red-600 flex items-center gap-2 bg-red-50 p-3 rounded-xl border border-red-100" data-id="element-6150">
              <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-6151" />
              <span className="font-medium" data-id="element-6152">{formError}</span>
            </div>}

          {linkWarnings.length > 0 && <div className="space-y-2" data-id="element-6153">
              {linkWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-xl text-sm ${w.type === 'grade_mismatch' ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`} data-id="element-6154">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6155" />
                  <p className="font-medium" data-id="element-6156">{w.message}</p>
                </div>)}
              {!confirmLink && <button onClick={() => {
          setConfirmLink(true);
          onLinkSelectedLearner();
        }} className="text-sm font-bold text-violet-600 hover:text-violet-800 underline hover:no-underline" data-id="element-6157">
                  Ja, toch toevoegen
                </button>}
            </div>}

          <div className="flex gap-3 pt-2" data-id="element-6158">
            <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-6159">
              Terug
            </Button>
            <Button type="button" onClick={onLinkSelectedLearner} disabled={!selectedLearner} className="flex-[2] bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40" data-id="element-6160">
              <LinkIcon className="w-4 h-4 mr-2" data-id="element-6161" />
              {selectedLearner ? `${selectedLearner.displayName} koppelen` : 'Selecteer een leerling'}
            </Button>
          </div>
        </div>}

      {linkMode === 'code' && <div className="space-y-4" data-id="element-6162">
          <p className="text-sm text-slate-500" data-id="element-6163">
            Heeft een leerling zelf een account aangemaakt? Vraag de koppelcode
            en voer deze hieronder in.
          </p>

          <div className="flex gap-3" data-id="element-6164">
            <Input variant="admin" size="lg" label="Koppelcode" id="linkCode" value={linkCodeInput} onChange={e => {
          setLinkCodeInput(e.target.value.toUpperCase());
          setFoundByCode(null);
          setFormError('');
        }} placeholder="Bijv. A3K7M2" className="flex-1 font-mono tracking-widest" autoFocus data-id="element-6165" />
          </div>

          {/* Found learner preview */}
          {foundByCode && <motion.div initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex items-center gap-3 p-4 bg-violet-50 border border-violet-200 rounded-xl" data-id="element-6166">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getAvatarGradient(foundByCode.displayName)} flex items-center justify-center text-white font-bold`} data-id="element-6167">
                {foundByCode.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1" data-id="element-6168">
                <div className="font-bold text-slate-900" data-id="element-6169">
                  {foundByCode.displayName}
                </div>
                <div className="text-xs text-slate-500" data-id="element-6170">
                  {foundByCode.username}
                  {foundByCode.grade ? ` · Groep ${foundByCode.grade}` : ''}
                </div>
              </div>
              <CheckCircle2 className="w-5 h-5 text-violet-600" data-id="element-6171" />
            </motion.div>}

          {formError && <div className="text-sm text-red-600 flex items-center gap-2 bg-red-50 p-3 rounded-xl border border-red-100" data-id="element-6172">
              <AlertCircle className="w-4 h-4 flex-shrink-0" data-id="element-6173" />
              <span className="font-medium" data-id="element-6174">{formError}</span>
            </div>}

          {linkWarnings.length > 0 && <div className="space-y-2" data-id="element-6175">
              {linkWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-xl text-sm ${w.type === 'grade_mismatch' ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`} data-id="element-6176">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-6177" />
                  <p className="font-medium" data-id="element-6178">{w.message}</p>
                </div>)}
              {!confirmLink && <button onClick={() => {
          setConfirmLink(true);
          onLinkSelectedLearner();
        }} className="text-sm font-bold text-violet-600 hover:text-violet-800 underline hover:no-underline" data-id="element-6179">
                  Ja, toch toevoegen
                </button>}
            </div>}

          <div className="flex gap-3 pt-2" data-id="element-6180">
            <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-6181">
              Terug
            </Button>
            {!foundByCode ? <Button type="button" onClick={onLookupCode} disabled={!linkCodeInput.trim()} className="flex-[2] bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40" data-id="element-6182">
                <Search className="w-4 h-4 mr-2" data-id="element-6183" />
                Code opzoeken
              </Button> : <Button type="button" onClick={onLinkSelectedLearner} className="flex-[2] bg-indigo-600 hover:bg-indigo-700 text-white" data-id="element-6184">
                <LinkIcon className="w-4 h-4 mr-2" data-id="element-6185" />
                {foundByCode.displayName} koppelen
              </Button>}
          </div>
        </div>}
    </motion.div>;
}